import { useRef } from 'react';
import { Avatar } from '@/components/Avatar';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { Dialog } from '@/ui/Dialog';
import { EstadoError } from '@/components/EstadoError';
import { Skeleton } from '@/ui/Skeleton';
import { useCursosDe, usePerfil, useSubirFoto } from './api';

/**
 * FichaDePersona.tsx — el modal con contacto, perfil profesional y cursos.
 *
 * ## El perfil y los cursos son dos consultas, no un `Promise.all`
 *
 * El vanilla pedía las dos juntas y esperaba a las dos:
 *
 *     const [profile, coursesData] = await Promise.all([…]);
 *
 * Un `Promise.all` se rechaza entero en cuanto falla una. Si
 * `/users/{id}/courses` daba error —o simplemente tardaba de más—, **la ficha no
 * se abría**: se perdían el nombre, el correo y el teléfono por no poder listar
 * los cursos, que es la parte menos urgente de las tres.
 *
 * Aquí son dos consultas independientes: el contacto se ve aunque los cursos
 * fallen, y los cursos dicen que fallaron en su propia tarjeta.
 */

export function FichaDePersona({
  id, alCerrar, puedeCambiarFoto,
}: {
  id: number | null;
  alCerrar: () => void;
  puedeCambiarFoto: boolean;
}) {
  const perfil = usePerfil(id);
  const cursos = useCursosDe(id);
  const subir = useSubirFoto();
  const selector = useRef<HTMLInputElement>(null);

  const p = perfil.data;

  function elegirFoto(archivo: File | undefined) {
    if (!archivo || id == null) return;
    // Cinco megas es lo que aceptaba el vanilla y lo que el servidor tolera sin
    // quejarse; comprobarlo aquí ahorra subir el archivo para que lo rechacen.
    if (archivo.size > 5 * 1024 * 1024) {
      Utils.toast('La imagen supera 5 MB', 'error');
      return;
    }
    subir.mutate(
      { userId: id, archivo },
      {
        onSuccess: () => Utils.toast('Foto actualizada', 'success'),
        onError: (e: unknown) =>
          Utils.toast((e as Error)?.message || 'No se pudo subir la foto', 'error'),
      },
    );
  }

  return (
    <Dialog
      abierto={id != null}
      onCambio={(v) => { if (!v) alCerrar(); }}
      titulo={p?.full_name || 'Ficha del colaborador'}
      ancho="max-w-2xl"
      pie={<Button variant="ghost" onClick={alCerrar}>Cerrar</Button>}
    >
      {perfil.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-20" />
          <Skeleton className="h-28" />
        </div>
      )}

      {perfil.isError && (
        <EstadoError
          err={perfil.error}
          que="la ficha"
          onReintentar={() => { perfil.refetch(); }}
        />
      )}

      {p && (
        <>
          <div className="mb-6 flex items-start gap-5">
            <Avatar
              nombre={p.full_name}
              foto={p.avatar_url}
              tamano={80}
              className="border-[3px] border-line text-8"
            />
            <div className="min-w-0 flex-1">
              <div className="text-5 font-extrabold text-brand-ink">{p.full_name}</div>
              {p.position && <div className="text-muted">{p.position}</div>}
              <div className="mt-2 flex flex-wrap gap-2 text-2 text-muted">
                {p.city && (
                  <span><i className="bi bi-geo-alt" aria-hidden="true" /> {p.city}</span>
                )}
                {p.birth_date_display && (
                  <span><i className="bi bi-cake" aria-hidden="true" /> {p.birth_date_display}</span>
                )}
              </div>
            </div>
          </div>

          <Card className="mb-4">
            <CardHeader
              titulo={<><i className="bi bi-person-lines-fill" aria-hidden="true" /> Datos de contacto</>}
            />
            <CardBody className="grid gap-3 sm:grid-cols-2">
              <Dato icono="bi-envelope" etiqueta="Email"
                valor={p.email} href={p.email ? `mailto:${p.email}` : undefined} />
              <Dato icono="bi-telephone" etiqueta="Teléfono"
                valor={p.phone} href={p.phone ? `tel:${p.phone}` : undefined} />
              {p.extension && (
                <Dato icono="bi-telephone-inbound" etiqueta="Extensión" valor={p.extension} />
              )}
            </CardBody>
          </Card>

          {(p.bio || p.skills) && (
            <Card className="mb-4">
              <CardHeader titulo={<><i className="bi bi-stars" aria-hidden="true" /> Perfil profesional</>} />
              <CardBody>
                {p.bio && <p className="text-3 text-ink-2">{p.bio}</p>}
                {p.skills && (
                  <div className={`flex flex-wrap gap-1.5 ${p.bio ? 'mt-3' : ''}`}>
                    {p.skills.split(',').map((s) => s.trim()).filter(Boolean).map((s, i) => (
                      <span key={`${s}-${i}`}
                        className="rounded bg-info-ink/12 px-2 py-0.5 text-1 text-info-ink">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          <div className="mb-4 flex flex-wrap gap-2">
            {/* `<a>` con las clases del portal, no `<Button>`: `ui/Button` no
                tiene `asChild`, y un botón que navega debe ser un enlace —se
                puede abrir en otra pestaña, se copia, y el lector de pantalla lo
                anuncia como enlace. Es el mismo criterio con el que esta feature
                reutiliza `card`, `table-wrap` y `search-bar`. */}
            {p.email && (
              <a href={`mailto:${p.email}`} className="btn btn-outline btn-sm">
                <i className="bi bi-envelope" aria-hidden="true" /> Enviar email
              </a>
            )}
            {p.phone && (
              <a href={`tel:${p.phone}`} className="btn btn-outline btn-sm">
                <i className="bi bi-telephone" aria-hidden="true" /> Llamar
              </a>
            )}
            {puedeCambiarFoto && (
              <>
                <Button size="sm" disabled={subir.isPending}
                  onClick={() => selector.current?.click()}>
                  <i className="bi bi-camera" aria-hidden="true" />
                  {subir.isPending ? 'Subiendo…' : 'Cambiar foto'}
                </Button>
                {/* El selector vive en el árbol en vez de crearse con
                    `document.createElement` en cada clic, como hacía el vanilla:
                    así se limpia solo al cerrar la ficha. */}
                <input
                  ref={selector}
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => { elegirFoto(e.target.files?.[0]); e.target.value = ''; }}
                />
              </>
            )}
          </div>

          <Cursos consulta={cursos} />
        </>
      )}
    </Dialog>
  );
}

function Dato({
  icono, etiqueta, valor, href,
}: {
  icono: string; etiqueta: string; valor?: string | null; href?: string;
}) {
  return (
    <div>
      <div className="mb-0.5 text-2 text-muted">
        <i className={`bi ${icono}`} aria-hidden="true" /> {etiqueta}
      </div>
      {valor ? (
        href
          ? <a href={href} className="text-3 font-semibold text-brand-ink">{valor}</a>
          : <span className="text-3 font-semibold text-brand-ink">{valor}</span>
      ) : (
        <span className="text-3 text-muted">—</span>
      )}
    </div>
  );
}

function Cursos({ consulta }: {
  consulta: ReturnType<typeof useCursosDe>;
}) {
  if (consulta.isLoading) return <Skeleton className="h-20" />;
  if (consulta.isError) {
    return (
      <Card>
        <CardHeader titulo={<><i className="bi bi-play-circle" aria-hidden="true" /> Cursos tomados</>} />
        <CardBody>
          <p className="text-3 text-muted">
            No se pudieron cargar los cursos. El resto de la ficha sí está.
          </p>
        </CardBody>
      </Card>
    );
  }
  const lista = consulta.data ?? [];
  if (!lista.length) {
    return <p className="text-center text-3 text-muted">Sin cursos registrados</p>;
  }
  return (
    <Card>
      <CardHeader titulo={<><i className="bi bi-play-circle" aria-hidden="true" /> Cursos tomados</>} />
      <CardBody>
        {lista.map((e) => (
          <div key={e.course_id}
            className="flex items-center justify-between border-b border-line py-2 last:border-0">
            <div className="min-w-0">
              <div className="truncate text-3 font-semibold">
                {e.course_title || `Curso #${e.course_id}`}
              </div>
              <div className={`text-2 ${e.completed_at ? 'text-ok-ink' : 'text-muted'}`}>
                {e.completed_at ? (
                  <><i className="bi bi-check-circle-fill" aria-hidden="true" />{' '}
                    Completado {Utils.fmtDate(e.completed_at)}</>
                ) : (
                  /* `?? 0`: el vanilla hacía `Math.round(e.progress_pct)` y con
                     `null` pintaba «NaN% completado». */
                  <><i className="bi bi-hourglass-split" aria-hidden="true" />{' '}
                    {Math.round(e.progress_pct ?? 0)}% completado</>
                )}
              </div>
            </div>
            {e.has_certificate && (
              <span className="shrink-0 rounded-sm bg-ok-ink/12 px-2 py-0.5 text-2 text-ok-ink">
                <i className="bi bi-award-fill" aria-hidden="true" /> Certificado
              </span>
            )}
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
